/**
 * OM SDK Web Video bootstrap.
 *
 * When IMA SDK is available, it already handles the OMID flow internally -
 * the only extra work we do here is load the service JS explicitly so the
 * runner can trace the network request in Playwright. For Tizen (where
 * there is no IMA SDK), we create the AdSession manually from a parsed
 * VAST AdVerifications list.
 *
 * OM SDK documentation: https://iabtechlab.com/wp-content/uploads/2019/03/OMSDK-JS-API-v1.1.pdf
 * Service URL:         https://cdn.omsdk.iabtechlab.com/1/omweb-v1.js
 */

const OM_SERVICE_URL = 'https://cdn.omsdk.iabtechlab.com/1/omweb-v1.js';
const PARTNER_NAME = 'cesp-testapp';
const PARTNER_VERSION = '1.0.0';

let serviceLoadPromise = null;

/**
 * Load the OM Web service JS once, then resolve with the `OmidSessionClient`
 * factory. Subsequent callers share the same promise.
 */
function loadOmService() {
  if (serviceLoadPromise) return serviceLoadPromise;
  serviceLoadPromise = new Promise((resolve, reject) => {
    if (window.OmidSessionClient) {
      resolve(window.OmidSessionClient);
      return;
    }
    const s = document.createElement('script');
    s.src = OM_SERVICE_URL;
    s.async = true;
    s.onload = () => {
      if (window.OmidSessionClient) {
        resolve(window.OmidSessionClient);
      } else {
        reject(new Error('OmidSessionClient not found after script load'));
      }
    };
    s.onerror = () => reject(new Error(`Failed to load ${OM_SERVICE_URL}`));
    document.head.appendChild(s);
  });
  return serviceLoadPromise;
}

/**
 * Create an OM SDK AdSession for the current ad and register verification
 * scripts. Safe to call multiple times per page - the session is rebuilt
 * from scratch for every new ad start event.
 *
 * @param {Object} opts
 * @param {HTMLVideoElement} opts.video        content video element
 * @param {HTMLElement}      opts.adContainer  IMA-managed ad container
 * @param {Array}            opts.verifications AdVerifications from VAST parse
 */
export async function bootstrapOmsdk(opts) {
  const { video, adContainer, verifications } = opts;
  const OmidSessionClient = await loadOmService();

  const Partner = OmidSessionClient.Partner;
  const Context = OmidSessionClient.Context;
  const VerificationScriptResource = OmidSessionClient.VerificationScriptResource;
  const AdSession = OmidSessionClient.AdSession;
  const AdEvents = OmidSessionClient.AdEvents;
  const MediaEvents = OmidSessionClient.MediaEvents;
  const VideoEvents = OmidSessionClient.VideoEvents || MediaEvents;

  const partner = new Partner(PARTNER_NAME, PARTNER_VERSION);

  const verificationResources = (verifications ?? []).map(
    (v) => new VerificationScriptResource(v.scriptUrl, v.vendorKey, v.params),
  );

  const context = new Context(partner, verificationResources, document.referrer || '');
  context.setVideoElement?.(video);
  context.setSlotElement?.(adContainer);

  const adSession = new AdSession(context);
  adSession.setCreativeType?.('video');
  adSession.setImpressionType?.('definedByJavascript');

  const adEvents = new AdEvents(adSession);
  const videoEvents = new VideoEvents(adSession);

  adSession.start();

  // Fire the OMID impression event the moment the creative starts.
  // When using IMA the SDK dispatches its own impression; ours here is
  // used only in Tizen mode where IMA is absent.
  try {
    adEvents.impressionOccurred();
  } catch {
    /* already fired by IMA, safe to ignore */
  }

  // Surface handles so Playwright can assert lifecycle if needed.
  window.__CESP_OMSDK__ = {
    adSession,
    adEvents,
    videoEvents,
    verifications: verificationResources,
  };

  return { adSession, adEvents, videoEvents };
}
